import { useCallback, useEffect, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, CurrencyInr, Cube, GitDiff, ListChecks, TreeStructure, Table, Package } from "@phosphor-icons/react";
import CostSummary from "./CostSummary";
import BomTree from "./BomTree";
import BomTable from "./BomTable";
import BomPrs from "./BomPrs";
import BomCompare from "./BomCompare";
import BomModelLink from "./BomModelLink";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function BomWorkspace({ projectId, me }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("tree"); // tree | table | prs | compare
  const [busy, setBusy] = useState("");
  const [verId, setVerId] = useState(null);   // viewing a frozen version
  const [verItems, setVerItems] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/bom/${projectId}`);
      setData(d);
      setVerId(null);
      setVerItems(null);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy("gen");
    try {
      const { data: r } = await api.post(`/bom/${projectId}/generate`, {}, { timeout: 90000 });
      toast.success(`BOM v${r.version}: ${r.summary.item_count} items · ${r.prs_created} PRs created`
        + (r.manual_edits_preserved ? ` · ${r.manual_edits_preserved} manual edits preserved` : ""));
      setDismissed(false);
      await load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const refreshCosts = async () => {
    setBusy("costs");
    try {
      const { data: r } = await api.post(`/bom/${projectId}/refresh-costs`);
      toast.success(`Costs refreshed — ${r.updated} items updated`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const saveItem = async (itemNumber, field, value) => {
    try {
      const { data: r } = await api.put(`/bom/${projectId}/items/${itemNumber}`, { [field]: value });
      if (r.changed) {
        setData((d) => ({
          ...d,
          bom: { ...d.bom, summary: r.summary, items: d.bom.items.map((i) => (i.item_number === itemNumber ? r.item : i)) },
          edits_since_generation: (d.edits_since_generation || 0) + 1,
        }));
        toast.success("Saved");
      }
    } catch (e) { toast.error(fmtErr(e)); load(); }
  };

  const viewVersion = async (v) => {
    if (!v) { setVerId(null); setVerItems(null); return; }
    try {
      const { data: vd } = await api.get(`/bom/${projectId}/versions/${v}`);
      setVerId(v);
      setVerItems(vd.items_snapshot);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const restore = async (v) => {
    try {
      const { data: r } = await api.post(`/bom/${projectId}/versions/${v}/restore`);
      toast.success(`Restored as BOM v${r.version}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const bom = data?.bom;
  const items = verItems || bom?.items || [];
  const versions = data?.versions || [];
  const outdated = data?.outdated && !dismissed;

  return (
    <div className="space-y-3" data-testid="bom-workspace">
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="generate-bom-btn" onClick={generate} disabled={!!busy}
          className="h-8 px-3 bg-[var(--c-accent)] hover:bg-[var(--c-accent)]/85 text-white text-xs rounded-sm flex items-center gap-1.5 disabled:opacity-50">
          <ArrowsClockwise size={13} className={busy === "gen" ? "animate-spin" : ""} />
          {busy === "gen" ? "Generating…" : bom ? "Regenerate BOM" : "Generate BOM"}
        </button>
        <button data-testid="refresh-costs-btn" onClick={refreshCosts} disabled={!!busy || !bom}
          className="h-8 px-3 border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] text-xs rounded-sm flex items-center gap-1.5 disabled:opacity-40">
          <CurrencyInr size={13} /> Refresh Costs
        </button>
        {versions.length > 0 && (
          <select data-testid="bom-version-select" value={verId || ""} onChange={(e) => viewVersion(e.target.value ? Number(e.target.value) : null)}
            className="h-8 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2">
            <option value="">● v{bom?.active_version} — Current (live)</option>
            {versions.map((v) => (
              <option key={v.version} value={v.version}>
                v{v.version} — {v.label} ({String(v.generated_at || "").slice(0, 10)})
              </option>
            ))}
          </select>
        )}
        {verId && (
          <button data-testid="bom-restore-btn" onClick={() => restore(verId)}
            className="h-8 px-3 border border-[var(--c-amber)]/50 text-[var(--c-amber)] text-xs rounded-sm">
            Restore v{verId}
          </button>
        )}
        <div className="ml-auto flex gap-1.5">
          {["xlsx", "pdf", "csv", "xml"].map((f) => (
            <button key={f} data-testid={`export-${f}-btn`} disabled={!bom}
              onClick={() => window.open(`${BASE}/api/bom/${projectId}/export.${f}`, "_blank")}
              className="h-8 px-2.5 border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] text-[10px] font-mono uppercase rounded-sm disabled:opacity-40">
              {f}
            </button>
          ))}
        </div>
      </div>

      {outdated && (
        <div className="flex items-start gap-3 bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-3" data-testid="bom-outdated-banner">
          <span className="text-[var(--c-amber)] text-sm">⚠</span>
          <div className="flex-1">
            <div className="text-[11px] text-[var(--c-text)]">
              Design updated (v{bom.source_design_version} → v{data.design_version}) since this BOM was generated. Quantities, weights and specs may be outdated.
            </div>
            <div className="flex gap-2 mt-2">
              <button data-testid="banner-regenerate-btn" onClick={generate}
                className="h-6 px-2.5 bg-[var(--c-amber)] text-white text-[10px] rounded-sm">Regenerate BOM</button>
              <button data-testid="banner-dismiss-btn" onClick={() => setDismissed(true)}
                className="h-6 px-2.5 text-[var(--c-muted)] hover:text-[var(--c-text)] text-[10px]">Dismiss</button>
            </div>
          </div>
        </div>
      )}
      {data?.outdated && dismissed && (
        <div className="text-[10px] font-mono text-[var(--c-amber)]" data-testid="bom-outdated-indicator">
          ⚠ Based on design v{bom?.source_design_version} (current: v{data.design_version})
        </div>
      )}

      {bom && <CostSummary summary={verId ? null : bom.summary} categories={bom.categories} template={data.template} />}
      {bom && (data.edits_since_generation > 0) && !verId && (
        <div className="text-[10px] font-mono text-[var(--c-amber)]" data-testid="bom-edits-badge">
          ✎ {data.edits_since_generation} manual edit(s) since v{bom.active_version}
        </div>
      )}

      <div className="flex gap-1 border-b border-[var(--c-border)]" data-testid="bom-view-tabs">
        {[["tree", TreeStructure, "Tree"], ["table", Table, "Table"], ["model", Cube, "3D Model"], ["prs", Package, "Purchase Requisitions"], ["compare", GitDiff, "Compare"]].map(([k, Icon, l]) => (
          <button key={k} data-testid={`bom-view-${k}`} onClick={() => setView(k)}
            className={`h-8 px-3 text-[11px] flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${
              view === k ? "border-[var(--c-accent)] text-[var(--c-text)]" : "border-transparent text-[var(--c-muted)] hover:text-[var(--c-text)]"}`}>
            <Icon size={13} /> {l}
          </button>
        ))}
      </div>

      {!bom && !["compare", "model"].includes(view) && (
        <div className="bg-[var(--c-surface)] border border-dashed border-[var(--c-border)] rounded-sm p-12 text-center" data-testid="bom-empty">
          <ListChecks size={26} className="mx-auto text-[var(--c-muted)]" />
          <div className="text-sm text-[var(--c-text)] mt-3">No BOM generated yet</div>
          <div className="text-xs text-[var(--c-muted)] mt-1">Run the Design Engine calculation, then Generate BOM to build the bill of materials from design data.</div>
        </div>
      )}

      {view === "tree" && bom && (
        <>
          {verId && <div className="text-[10px] font-mono text-[var(--c-amber)]" data-testid="bom-version-banner">Viewing frozen v{verId} (read-only). Select "Current (live)" to edit.</div>}
          <BomTree template={data.template} items={items} categories={bom.categories}
            onSave={verId ? () => toast.error("Frozen version — restore it first to edit") : saveItem} />
        </>
      )}
      {view === "table" && bom && (
        <BomTable items={items} onSave={verId ? () => toast.error("Frozen version — restore it first to edit") : saveItem} />
      )}
      {view === "model" && <BomModelLink projectId={projectId} />}
      {view === "prs" && <BomPrs projectId={projectId} me={me} />}
      {view === "compare" && <BomCompare projectId={projectId} versions={versions} liveEdits={data?.edits_since_generation} />}
    </div>
  );
}
